import React from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

type StepIconProps = { isActive: boolean; isCompleted: boolean };

const StepIcon = ({ isActive, isCompleted }: StepIconProps) => {
  if (isActive) {
    return <Icons.EditPencil customClass="w-4 h-4" />;
  }

  if (isCompleted) {
    return <Icons.Check customClass="w-4 h-4 text-navy-500" />;
  }

  return <Icons.CircleDotted customClass="w-4 h-4 text-navy-500" />;
};

type Step = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

type AidStudioStepperProps = {
  steps: Step[];
  activeStep: string;
  onStepChange?: (value: string) => void;
  completedSteps?: string[];
};

const AidStudioStepper = ({
  steps,
  activeStep,
  onStepChange,
  completedSteps = [],
}: AidStudioStepperProps) => {
  const activeIndex = steps.findIndex((s: Step) => s.value === activeStep);

  const handleStepClick = (step: Step) => {
    if (!step.isDisabled && onStepChange) {
      onStepChange(step.value);
    }
  };

  return (
    <nav
      className="flex gap-2 items-center py-1 rounded-md"
      data-testid="stepper"
    >
      {steps.map((step: Step, index: number) => {
        const isActive = activeStep === step.value;
        const isCompleted =
          completedSteps.includes(step.value) || index < activeIndex;
        const isDisabled = step.isDisabled && !isCompleted;
        const isClickable = !isDisabled && onStepChange;
        const isLast = index === steps.length - 1;

        const labelClass = classNames(
          'text-base leading-6 whitespace-nowrap text-center',
          {
            'font-semibold text-navy-800': isActive,
            'font-normal text-navy-500': !isActive,
          }
        );

        const stepContent = (
          <>
            <StepIcon isActive={isActive} isCompleted={isCompleted} />
            <span className={labelClass}>{step.label}</span>
          </>
        );

        return (
          <React.Fragment key={step.value}>
            {isClickable ? (
              <button
                type="button"
                onClick={() => handleStepClick(step)}
                className="flex gap-1 items-center bg-transparent border-none p-0 cursor-pointer hover:opacity-80"
                data-testid={`step-${step.value}`}
              >
                {stepContent}
              </button>
            ) : (
              <div
                className="flex gap-1 items-center"
                data-testid={`step-${step.value}`}
              >
                {stepContent}
              </div>
            )}
            {!isLast && (
              <Icons.ChevronRight customClass="w-3.5 h-3.5 text-navy-200 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default AidStudioStepper;
